import { InlineNotification, Stack, TextInput } from "@carbon/react";
import { capitalize } from "@flaminem/utils";
import React from "react";
import {
  FieldErrors,
  FieldValue,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";

import { RadioBooleanFormField } from "src/components/Form/RadioBooleanFormField";
import { ConfirmActionModal, PassiveModal } from "src/components/Modal";
import { ConfListSinglePickerFormField } from "src/components/Pickers/ConfListSinglePickerFormField";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors";
import { PropertyValueInput } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { createBlacklistElementMutation } from "src/graphql/anti-fraud/queries/blacklists/mutations/CreateBlacklistElementMutation";
import { updateBlacklistElementMutation } from "src/graphql/anti-fraud/queries/blacklists/mutations/UpdateBlacklistElementMutation.ts";
import { usePerformMutation } from "src/graphql/utils";
import { useT } from "src/internationalization";
import { useListSchema } from "src/pages/Blacklists/useListSchema.ts";
import { ClientSchemaBlacklist } from "src/types/clientSchema";
import { ConfListElement } from "src/types/confList";
import { Property } from "src/types/property";

export type BlacklistElementModalItem = {
  properties: { property: Property; value: string }[];
  sourceProperties: string[];
  id: string;
};
export type BlacklistElementModalProps = {
  onClose: () => void;
  onSuccess: () => void;

  item?: BlacklistElementModalItem;
  /** When provided, overrides the URL-based schema lookup. */
  listSchema?: ClientSchemaBlacklist;
  /** When provided, called with form values instead of running mutations. */
  onSave?: (values: PropertyValueInput[]) => void;
  /** When true, only mandatory properties are shown (used for "Compléter" flow). */
  showOnlyMandatory?: boolean;
  /** Override the modal title. */
  title?: string;
  /** Override the modal subtitle. */
  subtitle?: string;
  editProperties?: Property[] | null;
};
export type BlacklistElementForm = Record<string, string | ConfListElement>;

export const BlacklistElementModal: React.FC<BlacklistElementModalProps> = ({
  onClose,
  onSuccess,
  item,
  listSchema: listSchemaProp,
  onSave,
  showOnlyMandatory = false,
  title: titleProp,
  subtitle: subtitleProp,
  editProperties,
}) => {
  const { listSchema: listSchemaFromHook } = useListSchema();

  const listSchema = listSchemaProp ?? listSchemaFromHook;

  const { showSuccessToast } = useToast();
  const t = useT();

  const isCreateMode = !item;

  const methods = useForm<BlacklistElementForm>({
    defaultValues: isCreateMode
      ? {}
      : editProperties
      ? editProperties.reduce(
          (acc, prop) => ({
            ...acc,
            [prop.code]: item.properties.find((p) => p.property.code === prop.code)?.value ?? "",
          }),
          {}
        )
      : listSchema?.properties.reduce(
          (acc, prop) => ({
            ...acc,
            [prop.code]: item.properties.find((p) => p.property.code === prop.code)?.value ?? "",
          }),
          {}
        ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const {
    performMutation: createElement,
    loading: createLoading,
    error: createError,
  } = usePerformMutation(createBlacklistElementMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.blacklists.modal.create.toast.success.message"));
      onSuccess();
      onClose();
    },
  });

  const {
    performMutation: updateElement,
    loading: updateLoading,
    error: updateError,
  } = usePerformMutation(updateBlacklistElementMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.blacklists.modal.update.toast.success.message"));
      onSuccess();
      onClose();
    },
  });

  const loading = isCreateMode ? createLoading : updateLoading;
  const error = isCreateMode ? createError : updateError;

  const onSubmit: SubmitHandler<BlacklistElementForm> = async (data) => {
    if (!listSchema) {
      console.error("listSchema is expected to be defined, but is not");
      return;
    }
    const payload: PropertyValueInput[] = Object.keys(data)
      .filter((key) => data[key] != null)
      .map((key) => {
        if (typeof data[key] === "string") {
          return {
            code: key,
            value: data[key],
          };
        }
        return {
          code: key,
          value: (data[key] as ConfListElement).code,
        };
      });

    if (onSave) {
      onSave(payload);
      onClose();
      return;
    }

    if (isCreateMode) {
      await createElement({ properties: payload, blacklistCode: listSchema.code });
    } else {
      await updateElement({
        id: item.id,
        properties: payload.filter((p) => !item.sourceProperties.includes(p.code)),
      });
    }
  };

  const getInvalidText = (errors: FieldErrors<FieldValue<FieldValues>>, field: string) => {
    const fieldError = errors[field];
    switch (fieldError?.type) {
      case "required":
        return t("page.blacklists.modal.create.form.field.invalid");
      case "constraint": {
        const message = fieldError?.message;
        return typeof message === "string" && message.length > 1
          ? message
          : t("page.blacklists.modal.create.form.field.invalid.format");
      }
      default:
        return;
    }
  };

  const getLabel = (prop: Property) =>
    prop.mandatory ? `${capitalize(prop.name)} *` : capitalize(prop.name);
  const getInputFromProperty = (prop: Property) => {
    switch (prop.dataType) {
      case "string": {
        const isDisabled: boolean = !isCreateMode && item.sourceProperties.includes(prop.code);

        return (
          <TextInput
            key={prop.code}
            id={prop.code}
            data-input
            labelText={getLabel(prop)}
            invalid={!!errors?.[prop.code]}
            invalidText={getInvalidText(errors, prop.code)}
            disabled={isDisabled}
            {...register(prop.code, {
              required: {
                value: prop.mandatory,
                message: t("page.blacklists.modal.create.form.field.invalid"),
              },
              validate: {
                constraint: (val) =>
                  prop.constraints.every(
                    (constraint) =>
                      typeof val === "string" && RegExp(constraint.value ?? "").test(val)
                  ) ||
                  prop.constraints
                    .map((c) => c.message)
                    .join(
                      " " + t("page.blacklists.modal.create.form.field.constraints.divider") + " "
                    ),
              },
            })}
          />
        );
      }
      case "integer": {
        const isDisabled: boolean = !isCreateMode && item.sourceProperties.includes(prop.code);

        return (
          <TextInput
            key={prop.code}
            id={prop.code}
            data-input
            labelText={getLabel(prop)}
            invalid={!!errors?.[prop.code]}
            invalidText={getInvalidText(errors, prop.code)}
            disabled={isDisabled}
            {...register(prop.code, {
              required: {
                value: prop.mandatory,
                message: t("page.blacklists.modal.create.form.field.invalid"),
              },
              validate: {
                constraint: (val) => {
                  return !isNaN(Number(val));
                },
              },
            })}
          />
        );
      }
      case "boolean": {
        const isDisabled: boolean = !isCreateMode && item.sourceProperties.includes(prop.code);

        return (
          <RadioBooleanFormField
            key={prop.code}
            id={prop.code}
            name={prop.code}
            label={getLabel(prop)}
            control={methods.control}
            disabled={isDisabled}
            rules={{
              required: {
                value: prop.mandatory,
                message: t("page.blacklists.modal.create.form.field.invalid"),
              },
            }}
          />
        );
      }
      case "code": {
        return prop.confListCode ? (
          <ConfListSinglePickerFormField
            name={prop.code}
            method={methods}
            confListCode={prop.confListCode}
            label={getLabel(prop)}
            rules={{
              required: {
                value: prop.mandatory,
                message: t("page.blacklists.modal.create.form.field.invalid"),
              },
            }}
          />
        ) : null;
      }
    }
  };

  if (!listSchema) {
    console.error("listSchema is expected to be defined, but is not");
    return null;
  }

  const visibleProperties = showOnlyMandatory
    ? listSchema.properties.filter((p) => p.mandatory)
    : listSchema.properties;

  const propertiesToDisplay = editProperties ?? visibleProperties;

  // Empty state check only for update mode (not applicable in onSave context)
  if (!isCreateMode && !onSave) {
    const isEmptyState = listSchema.properties.every((prop) =>
      item.sourceProperties.includes(prop.code)
    );

    if (isEmptyState) {
      return (
        <PassiveModal
          size="sm"
          title={t("page.blacklists.modal.update.title", {
            type: listSchema.name,
            value:
              item.properties.find((p) => listSchema.properties[0].code === p.property.code)
                ?.value ?? "",
          })}
          onClose={onClose}
        >
          <FormProvider {...methods}>
            <Stack gap={5}>
              <InlineNotification kind="warning" hideCloseButton>
                {t("page.blacklists.modal.create.form.emptyState")}
              </InlineNotification>
              {propertiesToDisplay.map((prop, index) => {
                return <div key={index}>{getInputFromProperty(prop)}</div>;
              })}
            </Stack>
          </FormProvider>
        </PassiveModal>
      );
    }
  }

  const resolvedTitle =
    titleProp ??
    (isCreateMode
      ? t("page.blacklists.modal.create.title")
      : t("page.blacklists.modal.update.title", {
          type: listSchema.name,
          value:
            item.properties.find((p) => listSchema.properties[0].code === p.property.code)?.value ??
            "",
        }));

  const resolvedSubtitle =
    subtitleProp ??
    (!isCreateMode
      ? item.properties
          .find((p) => listSchema.properties[0].code === p.property.code)
          ?.value.toString()
      : undefined);

  return (
    <ConfirmActionModal
      size="sm"
      error={mapToModalError(onSave ? null : error)}
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
      loading={onSave ? false : loading}
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText={t("general.actions.confirm")}
      initialFocus={
        isCreateMode ? "[data-input]:first-child" : "[data-input]:not(:disabled):first-child"
      }
      confirmButtonDisabled={!onSave && !isCreateMode && !isDirty}
    >
      <FormProvider {...methods}>
        <Stack gap={5}>
          {propertiesToDisplay.map((prop, index) => {
            return <div key={index}>{getInputFromProperty(prop)}</div>;
          })}
        </Stack>
      </FormProvider>
    </ConfirmActionModal>
  );
};
